import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Share2, RotateCcw, Trophy, Clock, LogIn, Link, User, X, Star, Sparkles, Target, CheckCircle, BarChart3 } from 'lucide-react';
import { useGame } from '../context/GameContext';
import { useAuth } from '../hooks/useAuth';
import PerformanceHistogram from './PerformanceHistogram';

interface CompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRestart: () => void;
}

const CompletionModal: React.FC<CompletionModalProps> = ({ isOpen, onClose, onRestart }) => {
  const { gameState } = useGame();
  const { signInWithGoogle, linkWithGoogle, isAnonymous, isAuthenticated } = useAuth();
  const [countdown, setCountdown] = useState('');
  

  const getCompletionMessage = () => {
    const messages = {
      0: "1st Circle: Perfect!",
      1: "2nd Circle: Great!",
      2: "3rd Circle: Good job!",
      3: "4th Circle: Phew!",
      4: "5th Circle: Next time!",
    };
    return messages[gameState.errors as keyof typeof messages] || "Game Over!";
  };

  const getCompletionEmoji = () => {
    const emojis = {
      0: "🏆",
      1: "🥈",
      2: "🥉",
      3: "💪",
      4: "🎯",
    };
    return emojis[gameState.errors as keyof typeof emojis] || "🎮";
  };

  useEffect(() => {
    if (!isOpen) return;

    const updateCountdown = () => {
      const now = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      
      const diff = tomorrow.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Pathways',
          text: gameState.shareMessage,
        });
      } catch (error) {
        handleCopyToClipboard();
      }
    } else {
      handleCopyToClipboard();
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(gameState.shareMessage);
      // You could add a toast notification here
      alert('Results copied to clipboard!');
    } catch (error) {
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
    }
  };

  const handleLinkAccount = async () => {
    try {
      await linkWithGoogle();
    } catch (error) {
    }
  };

  const getGradientByErrors = () => {
    // Match new circle color progression: center purple → blue → green → yellow → red
    const gradients = {
      0: "from-purple-400 via-purple-500 to-purple-600", // Perfect - purple (center circle)
      1: "from-blue-400 via-blue-500 to-blue-600", // 1 error - blue
      2: "from-emerald-400 via-emerald-500 to-emerald-600", // 2 errors - green
      3: "from-amber-400 via-amber-500 to-amber-600", // 3 errors - yellow
      4: "from-red-400 via-red-500 to-red-600", // 4 errors - red
    };
    return gradients[gameState.errors as keyof typeof gradients] || "from-red-500 to-red-600";
  };

  const getPerformanceColor = () => {
    // Match new circle color progression
    const colors = {
      0: "text-purple-600", // Perfect - purple
      1: "text-blue-600", // 1 error - blue
      2: "text-emerald-600", // 2 errors - green
      3: "text-amber-600", // 3 errors - yellow
      4: "text-red-600", // 4 errors - red
    };
    return colors[gameState.errors as keyof typeof colors] || "text-red-600";
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 z-50 overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Animated Header */}
        <motion.div 
          className="flex-shrink-0 relative"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <div className="bg-white/80 backdrop-blur-xl border-b border-white/20 shadow-lg relative overflow-hidden">
            {/* Animated background particles */}
            <motion.div
              className="absolute inset-0 opacity-20"
              animate={{ 
                background: [
                  "radial-gradient(circle at 20% 20%, #3B82F6 0%, transparent 50%)",
                  "radial-gradient(circle at 80% 80%, #8B5CF6 0%, transparent 50%)",
                  "radial-gradient(circle at 40% 60%, #EC4899 0%, transparent 50%)",
                ]
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            />
            
            <div className="relative z-10 flex items-center justify-between p-6">
              <div className="flex items-center gap-4">
                <motion.div 
                  className={`p-3 bg-gradient-to-br ${getGradientByErrors()} rounded-2xl`}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 300, delay: 0.3 }}
                >
                  <Trophy className="w-8 h-8 text-white" />
                </motion.div>
                <div>
                  <motion.h1 
                    className="text-3xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    {getCompletionMessage()}
                  </motion.h1>
                  <motion.p 
                    className="text-slate-500 font-medium"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    Daily challenge completed!
                  </motion.p>
                </div>
              </div>
              <motion.button
                onClick={onClose}
                className="p-4 text-slate-400 hover:text-slate-600 hover:bg-white/50 rounded-2xl transition-all backdrop-blur-sm group"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
              >
                <X size={24} />
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="max-w-2xl mx-auto space-y-8">
              {/* Hero Section with Big Emoji */}
              <motion.div
                className="text-center"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.7 }}
              >
                <motion.div 
                  className="text-8xl mb-4 relative"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  {getCompletionEmoji()}
                  {gameState.errors === 0 && (
                    <motion.div
                      className="absolute -top-4 -right-4 text-2xl"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1, type: "spring", stiffness: 300 }}
                    >
                      ✨
                    </motion.div>
                  )}
                </motion.div>
              </motion.div>

              {/* Performance Stats Card */}
              <motion.div 
                className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 bg-gradient-to-br ${getGradientByErrors()} rounded-xl`}>
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Today's Performance</h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Errors */}
                  <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Trophy size={20} className={getPerformanceColor()} />
                      <span className="font-semibold text-slate-700">Errors</span>
                    </div>
                    <div className={`text-2xl font-bold ${getPerformanceColor()}`}>
                      {gameState.errors} / {gameState.maxErrors}
                    </div>
                  </div>

                  {/* Next Game Countdown */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Clock size={20} className="text-purple-600" />
                      <span className="font-semibold text-slate-700">Next Game</span>
                    </div>
                    <div className="text-lg font-bold text-purple-600 font-mono">
                      {countdown}
                    </div>
                  </div>
                </div>

                {/* Perfect game celebration */}
                {gameState.errors === 0 && (
                  <motion.div
                    className="mt-4 p-4 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-2xl border border-yellow-200"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.2, type: "spring", stiffness: 300 }}
                  >
                    <div className="flex items-center justify-center gap-2 text-yellow-700">
                      <Sparkles size={20} />
                      <span className="font-bold">Perfect Game! You're a Pathways Master! 🎉</span>
                    </div>
                  </motion.div>
                )}
              </motion.div>

              {/* Performance History Card */}
              <motion.div 
                className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Performance History</h3>
                </div>

                <PerformanceHistogram className="" />
              </motion.div>

              {/* Account Section */}
              {(!isAuthenticated || (isAuthenticated && isAnonymous)) && (
                <motion.div 
                  className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20"
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.9 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">Save Your Progress</h3>
                  </div>

                  {!isAuthenticated ? (
                    <motion.button
                      onClick={handleGoogleSignIn}
                      className="w-full flex items-center gap-4 p-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl hover:shadow-xl transition-all group"
                      whileHover={{ scale: 1.02, y: -3 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="p-3 bg-white/20 rounded-xl group-hover:bg-white/30 transition-colors">
                        <LogIn size={24} />
                      </div>
                      <div className="text-left flex-1">
                        <p className="font-bold text-lg">Sign in with Google</p>
                        <p className="text-blue-100">Save your progress across devices</p>
                      </div>
                    </motion.button>
                  ) : (
                    <motion.button
                      onClick={handleLinkAccount}
                      className="w-full flex items-center gap-4 p-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl hover:shadow-xl transition-all group"
                      whileHover={{ scale: 1.02, y: -3 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="p-3 bg-white/20 rounded-xl group-hover:bg-white/30 transition-colors">
                        <Link size={24} />
                      </div>
                      <div className="text-left flex-1">
                        <p className="font-bold text-lg">Link Google Account</p>
                        <p className="text-blue-100">Sync progress across devices</p>
                      </div>
                    </motion.button>
                  )}
                </motion.div>
              )}

              {/* Action Buttons */}
              <motion.div 
                className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.0 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">What's Next?</h3>
                </div>

                <div className="space-y-4">
                  {/* Share Button */}
                  <motion.button
                    onClick={handleShare}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 px-6 rounded-2xl font-bold flex items-center justify-center gap-3 hover:shadow-xl transition-all relative overflow-hidden group"
                    whileHover={{ scale: 1.02, y: -3 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.1 }}
                  >
                    <motion.div 
                      className="p-2 bg-white/20 rounded-xl"
                      whileHover={{ rotate: -10, scale: 1.1 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      <Share2 size={20} />
                    </motion.div>
                    <span className="text-lg">Share Results</span>
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      initial={false}
                    />
                  </motion.button>

                  {/* Action Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <motion.button
                      onClick={onClose}
                      className="flex-1 bg-slate-100 text-slate-700 py-4 px-4 rounded-2xl font-bold hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.2 }}
                    >
                      <Star size={18} />
                      View Game
                    </motion.button>

                    <motion.button
                      onClick={onRestart}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 px-4 rounded-2xl font-bold flex items-center justify-center gap-2 hover:shadow-lg transition-all"
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.3 }}
                    >
                      <motion.div whileHover={{ rotate: -180 }} transition={{ duration: 0.3 }}>
                        <RotateCcw size={18} />
                      </motion.div>
                      Play Again
                    </motion.button>
                  </div>
                </div>
              </motion.div>

              {/* Fiction Diction Plug */}
              <motion.div 
                className="bg-white/40 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.4 }}
              >
                <div className="text-center">
                  <motion.a
                    href="https://fictiondiction.web.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-indigo-100 to-purple-100 text-slate-700 rounded-2xl hover:from-indigo-200 hover:to-purple-200 transition-all group"
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <img 
                      src="/fictiondiction.webp" 
                      alt="Fiction Diction" 
                      className="w-8 h-8 rounded-lg group-hover:scale-110 transition-transform"
                    />
                    <div className="text-left">
                      <div className="font-bold text-indigo-800">Try Fiction Diction!</div>
                      <div className="text-sm text-indigo-600">Fun party Fictionary game for 3+ people</div>
                    </div>
                  </motion.a>
                </div>
              </motion.div>

              {/* Bottom spacer */}
              <div className="h-8" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletionModal;